import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, doc, updateDoc, deleteDoc, writeBatch, Timestamp, addDoc, query, where } from 'firebase/firestore'; 
import { Bar, Line, Doughnut, Pie, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { locations } from './tanzaniaLocations';
import './AdminDashboard.css';
import { useAuth } from './App'; // Import the hook from App.js

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const logAction = async (action, entityId, userEmail, details = {}) => {
    try {
        await addDoc(collection(db, "audit_logs"), {
            action,
            entityId, // Generic ID for either accident or hazard
            userEmail: userEmail || 'Unknown User',
            timestamp: Timestamp.now(),
            details
        });
    } catch (error) {
        console.error("Error logging action: ", error);
    }
};

const AuditTrail = ({ accidentId }) => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!accidentId) return;
        const logsQuery = query(collection(db, "audit_logs"), where("entityId", "==", accidentId));
        const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
            const logsData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data(), timestamp: doc.data().timestamp?.toDate() }))
                .sort((a, b) => b.timestamp - a.timestamp);
            setLogs(logsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching audit logs: ", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [accidentId]);

    if (isLoading) return <p>Loading audit trail...</p>;

    return (
        <div className="audit-trail-container">
            <h4>History</h4>
            {logs.length === 0 ? (
                <p>No history recorded for this report.</p>
            ) : (
                <div className="audit-trail-list">
                    {logs.map(log => (
                        <div key={log.id} className="audit-trail-item">
                            <p><strong>{log.action}</strong> by {log.userEmail}</p>
                            <p className="timestamp">{log.timestamp.toLocaleString()}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const MergeModal = ({ reports, onSave, onClose, isSubmitting }) => {
    const [mergedData, setMergedData] = useState({});
    const [finalDescription, setFinalDescription] = useState('');

    useEffect(() => {
        if (reports.length > 0) {
            const initialMergeState = {};
            const fieldsToMerge = ['datetime', 'region', 'district', 'ward', 'location', 'source', 'sourceDetails'];
            
            fieldsToMerge.forEach(field => {
                initialMergeState[field] = reports[0][field] || '';
            });

            initialMergeState.injuriesMale = reports.reduce((sum, r) => sum + (r.injuriesMale || 0), 0);
            initialMergeState.injuriesFemale = reports.reduce((sum, r) => sum + (r.injuriesFemale || 0), 0);
            initialMergeState.injuriesChildren = reports.reduce((sum, r) => sum + (r.injuriesChildren || 0), 0);
            initialMergeState.fatalitiesMale = reports.reduce((sum, r) => sum + (r.fatalitiesMale || 0), 0);
            initialMergeState.fatalitiesFemale = reports.reduce((sum, r) => sum + (r.fatalitiesFemale || 0), 0);
            initialMergeState.fatalitiesChildren = reports.reduce((sum, r) => sum + (r.fatalitiesChildren || 0), 0);

            const allVehicles = reports.flatMap(r => r.vehicles || []);
            const uniqueVehicles = allVehicles.reduce((acc, current) => {
                if (!acc.find(item => item.registration === current.registration && item.type === current.type)) {
                    acc.push(current);
                }
                return acc;
            }, []);
            initialMergeState.vehicles = uniqueVehicles;

            const combinedDescription = reports.map(r => r.description || '').filter(d => d).join('\n\n---\n\n');
            setFinalDescription(combinedDescription);
            initialMergeState.description = combinedDescription;
            
            setMergedData(initialMergeState);
        }
    }, [reports]);

    const handleSelectionChange = (field, value) => {
        setMergedData(prev => ({ ...prev, [field]: value }));
    };

    const handleNumericSelection = (field, type, reportIndex = 0) => {
        let value;
        if (type === 'sum') {
            value = reports.reduce((sum, r) => sum + (r[field] || 0), 0);
        } else {
            value = reports[reportIndex][field] || 0;
        }
        handleSelectionChange(field, value);
    };
    
    const handleDescriptionChange = (e) => {
        setFinalDescription(e.target.value);
    };

    const handleSave = () => {
        onSave({ ...mergedData, description: finalDescription, isVerified: true, submittedAt: Timestamp.now() });
    };

    const MergeRow = ({ field, label }) => (
        <tr>
            <td>{label}</td>
            {reports.map((report, index) => (
                <td key={report.id}>
                    <label className={`merge-option ${mergedData[field] === report[field] ? 'selected' : ''}`}>
                        <input
                            type="radio"
                            name={field}
                            checked={mergedData[field] === report[field]}
                            onChange={() => handleSelectionChange(field, report[field])}
                        />
                        {report[field]?.toString() || <em>empty</em>}
                    </label>
                </td>
            ))}
        </tr>
    );
    
    const MergeNumericRow = ({ field, label }) => (
         <tr>
            <td>{label}</td>
            {reports.map((report, index) => (
                <td key={report.id}>
                    <label className={`merge-option ${mergedData[field] === (report[field] || 0) ? 'selected' : ''}`}>
                        <input
                            type="radio"
                            name={field}
                            checked={mergedData[field] === (report[field] || 0)}
                            onChange={() => handleNumericSelection(field, 'single', index)}
                        />
                        {report[field] || 0}
                    </label>
                </td>
            ))}
            <td>
                 <label className={`merge-option ${mergedData[field] === reports.reduce((sum, r) => sum + (r[field] || 0), 0) ? 'selected' : ''}`}>
                    <input
                        type="radio"
                        name={field}
                        checked={mergedData[field] === reports.reduce((sum, r) => sum + (r[field] || 0), 0)}
                        onChange={() => handleNumericSelection(field, 'sum')}
                    />
                    Sum ({reports.reduce((sum, r) => sum + (r[field] || 0), 0)})
                </label>
            </td>
        </tr>
    );


    return (
        <div className="modal-backdrop">
            <div className="modal-content merge-modal-content">
                <h2>Merge Duplicate Reports</h2>
                <p>Select the correct data for each field. The final merged report will be marked as verified, and the original reports will be deleted.</p>
                
                <div style={{ overflowX: 'auto' }}>
                    <table className="merge-table">
                        <thead>
                            <tr>
                                <th>Field</th>
                                {reports.map((r, i) => <th key={r.id}>Report #{i+1} (Source: {r.source})</th>)}
                                <th>Combined Option</th>
                            </tr>
                        </thead>
                        <tbody>
                            <MergeRow field="datetime" label="Date & Time" />
                            <MergeRow field="region" label="Region" />
                            <MergeRow field="district" label="District" />
                            <MergeRow field="ward" label="Ward" />
                            <MergeRow field="location" label="Specific Location" />
                            <MergeRow field="source" label="Primary Source" />
                            <MergeRow field="sourceDetails" label="Source Details" />
                            
                            <MergeNumericRow field="injuriesMale" label="Injuries (Male)" />
                            <MergeNumericRow field="injuriesFemale" label="Injuries (Female)" />
                            <MergeNumericRow field="injuriesChildren" label="Injuries (Children)" />
                            <MergeNumericRow field="fatalitiesMale" label="Fatalities (Male)" />
                            <MergeNumericRow field="fatalitiesFemale" label="Fatalities (Female)" />
                            <MergeNumericRow field="fatalitiesChildren" label="Fatalities (Children)" />
                            
                             <tr>
                                <td>Vehicles</td>
                                <td colSpan={reports.length + 1}>
                                    <p>All unique vehicles will be combined in the final report:</p>
                                    <ul>
                                        {mergedData.vehicles?.map((v, i) => <li key={i}>{v.type} ({v.registration || 'N/A'})</li>)}
                                    </ul>
                                </td>
                            </tr>
                            <tr>
                                <td>Description</td>
                                <td colSpan={reports.length + 1}>
                                    <textarea 
                                        rows="6" 
                                        style={{ width: '100%' }} 
                                        value={finalDescription}
                                        onChange={handleDescriptionChange}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} className="btn-cancel" disabled={isSubmitting}>Cancel</button>
                    <button onClick={handleSave} className="btn-save" disabled={isSubmitting}>
                        {isSubmitting ? 'Merging...' : 'Confirm and Merge Reports'}
                    </button>
                </div>
            </div>
        </div>
    );
};


const AccidentDetailsModal = ({ accident, onClose }) => {
    if (!accident) return null;
    const totalInjuries = accident.totalInjuries > 0 ? accident.totalInjuries : (accident.injuriesMale || 0) + (accident.injuriesFemale || 0) + (accident.injuriesChildren || 0);
    const totalFatalities = accident.totalFatalities > 0 ? accident.totalFatalities : (accident.fatalitiesMale || 0) + (accident.fatalitiesFemale || 0) + (accident.fatalitiesChildren || 0);
    return (
        <div className="modal-backdrop"><div className="modal-content">
            <h2>Accident Details (ID: {accident.id.substring(0, 8)}...)</h2>
            <div className="modal-body">
                <p><strong>Date & Time:</strong> {accident.datetime?.toLocaleString()}</p>
                <p><strong>Location:</strong> {`${accident.location}, ${accident.ward}, ${accident.district}, ${accident.region}`}</p><hr />
                <p><strong>Vehicles Involved:</strong></p>
                <ul>{accident.vehicles?.map((v, i) => (<li key={i}>{v.type} {v.registration && `(${v.registration})`} {v.ownerDetails && ` - Owner: ${v.ownerDetails}`}</li>))}</ul><hr />
                <p><strong>Casualties:</strong></p>
                <ul>
                    <li><strong>Total Injuries: {totalInjuries}</strong> (M: {accident.injuriesMale || 0}, F: {accident.injuriesFemale || 0}, C: {accident.injuriesChildren || 0})</li>
                    <li><strong>Total Fatalities: {totalFatalities}</strong> (M: {accident.fatalitiesMale || 0}, F: {accident.fatalitiesFemale || 0}, C: {accident.fatalitiesChildren || 0})</li>
                </ul><hr />
                <p><strong>Description:</strong></p><p className="description-box">{accident.description || "No description provided."}</p><hr />
                
                {accident.mediaUrls && accident.mediaUrls.length > 0 && (
                    <>
                        <p><strong>Attached Media:</strong></p>
                        <div className="media-gallery">
                            {accident.mediaUrls.map((url, index) => (
                                <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                                    <img src={url} alt={`accident media ${index + 1}`} />
                                </a>
                            ))}
                        </div>
                        <hr />
                    </>
                )}

                <p><strong>Source:</strong> {accident.source}</p><p><strong>Source Details:</strong> {accident.sourceDetails || "N/A"}</p>
                <AuditTrail accidentId={accident.id} />
            </div>
            <button onClick={onClose} className="modal-close-btn">Close</button>
        </div></div>
    );
};
const EditAccidentModal = ({ accident, onSave, onClose, isSubmitting }) => {
    const [formData, setFormData] = useState(accident);
    useEffect(() => {
        if (accident && accident.datetime) {
            const date = accident.datetime;
            const inplace = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            const hh = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            const localDateTime = `${inplace}-${mm}-${dd}T${hh}:${min}`;
            setFormData({...accident, datetime: localDateTime });
        } else {
            setFormData(accident);
        }
    }, [accident]);
    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
    };
    
    const handleLocationChange = (e) => {
        const { name, value } = e.target;
        let newFormData = { ...formData, [name]: value };
        if (name === 'region') { 
            newFormData.district = ''; 
            newFormData.ward = ''; 
        }
        if (name === 'district') { 
            newFormData.ward = value === 'Other' ? 'Other' : ''; 
        }
        setFormData(newFormData);
    };

    const handleVehicleChange = (index, event) => {
        const { name, value } = event.target;
        const newVehicles = formData.vehicles.map((vehicle, i) => index === i ? { ...vehicle, [name]: value } : vehicle);
        setFormData(prev => ({ ...prev, vehicles: newVehicles }));
    };
    const addVehicle = () => {
        const newVehicles = [...(formData.vehicles || []), { type: 'Private Vehicle', registration: '', ownerDetails: '' }];
        setFormData(prev => ({ ...prev, vehicles: newVehicles }));
    };
    const removeVehicle = (index) => {
        const newVehicles = formData.vehicles.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, vehicles: newVehicles }));
    };
    const handleSaveChanges = () => {
        const dataToSave = { ...formData, datetime: Timestamp.fromDate(new Date(formData.datetime)) };
        onSave(dataToSave);
    };
    if (!accident) return null;
    return (
        <div className="modal-backdrop"><div className="modal-content">
            <h2>Edit Accident Report</h2>
            <div className="modal-body edit-form">
                <fieldset><legend>Incident Details</legend>
                    <div className="form-group"><label>Date & Time</label><input type="datetime-local" name="datetime" value={formData.datetime || ''} onChange={handleChange} /></div>
                    <div className="form-group"><label>Source</label><select name="source" value={formData.source || 'User Input'} onChange={handleChange}><option>User Report</option><option>Social Media</option><option>News Outlet</option><option>Other</option></select></div>
                    <div className="form-group"><label>Source Details</label><input type="text" name="sourceDetails" value={formData.sourceDetails || ''} onChange={handleChange} /></div>
                </fieldset>
                <fieldset><legend>Location</legend>
                    <div className="form-group"><label>Region</label><select name="region" value={formData.region || ''} onChange={handleLocationChange}>{Object.keys(locations).map(r => <option key={r} value={r}>{r}</option>)}</select></div>
                    
                    <div className="form-group"><label>District</label>
                        <select name="district" value={formData.district || ''} onChange={handleLocationChange} disabled={!formData.region}>
                            <option value="" disabled>Select a District</option>
                            {formData.region && Object.keys(locations[formData.region]).map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>

                    <div className="form-group"><label>Ward</label>
                        <select name="ward" value={formData.ward || ''} onChange={handleChange} disabled={!formData.district || formData.district === 'Other'}>
                            {formData.district && formData.district !== 'Other' && locations[formData.region]?.[formData.district] &&
                                <>
                                    <option value="" disabled>Select a Ward</option>
                                    {locations[formData.region][formData.district].map(w => <option key={w} value={w}>{w}</option>)}
                                </>
                            }
                            {formData.district === 'Other' &&
                                <option key="other-ward" value="Other">Other</option>
                            }
                        </select>
                    </div>
                    <div className="form-group"><label>Specific Location</label><input type="text" name="location" value={formData.location || ''} onChange={handleChange} /></div>
                </fieldset>
                <fieldset><legend>Vehicles Involved</legend>
                    {formData.vehicles?.map((vehicle, index) => (
                        <div className="vehicle-entry-new" key={index}>
                            <div className="form-group"><label>Vehicle #{index + 1} Type</label>
                                <select name="type" value={vehicle.type} onChange={(e) => handleVehicleChange(index, e)}>
                                    <option>Private Vehicle</option>
                                    <option>Motorcycle (Bodaboda)</option>
                                    <option>Tricycle (Bajaji)</option>
                                    <option>Bus (Daladala)</option>
                                    <option>Regional Bus</option>
                                    <option>Lorry</option>
                                    <option>Bicycle</option>
                                    <option>Train</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div className="form-group"><label>Registration (if known)</label><input type="text" name="registration" placeholder="e.g., T 123 ABC" value={vehicle.registration || ''} onChange={(e) => handleVehicleChange(index, e)}/></div>
                            <div className="form-group"><label>Owner's Details (Optional)</label><input type="text" name="ownerDetails" placeholder="e.g., Name, Phone, or Company" value={vehicle.ownerDetails || ''} onChange={(e) => handleVehicleChange(index, e)}/></div>
                            {formData.vehicles.length > 1 && (<button type="button" className="remove-btn" onClick={() => removeVehicle(index)}>Remove</button>)}
                        </div>
                    ))}
                    <button type="button" className="add-btn" onClick={addVehicle}>+ Add Another Vehicle</button>
                </fieldset>
                <fieldset><legend>Casualties</legend>
                    <div className="casualty-group"><div className="form-group"><label>Injuries (Male)</label><input type="number" min="0" name="injuriesMale" value={formData.injuriesMale || 0} onChange={handleChange} /></div><div className="form-group"><label>Injuries (Female)</label><input type="number" min="0" name="injuriesFemale" value={formData.injuriesFemale || 0} onChange={handleChange} /></div><div className="form-group"><label>Injuries (Children)</label><input type="number" min="0" name="injuriesChildren" value={formData.injuriesChildren || 0} onChange={handleChange} /></div></div>
                     <div className="casualty-group"><div className="form-group"><label>Fatalities (Male)</label><input type="number" min="0" name="fatalitiesMale" value={formData.fatalitiesMale || 0} onChange={handleChange} /></div><div className="form-group"><label>Fatalities (Female)</label><input type="number" min="0" name="fatalitiesFemale" value={formData.fatalitiesFemale || 0} onChange={handleChange} /></div><div className="form-group"><label>Fatalities (Children)</label><input type="number" min="0" name="fatalitiesChildren" value={formData.fatalitiesChildren || 0} onChange={handleChange} /></div></div>
                     <hr/><div className="casualty-group"><div className="form-group"><label>Total Injuries (if breakdown unknown)</label><input type="number" min="0" name="totalInjuries" value={formData.totalInjuries || 0} onChange={handleChange} /></div><div className="form-group"><label>Total Fatalities (if breakdown unknown)</label><input type="number" min="0" name="totalFatalities" value={formData.totalFatalities || 0} onChange={handleChange} /></div></div>
                </fieldset>
                <fieldset><legend>Description</legend>
                    <div className="form-group"><label>Brief Description</label><textarea name="description" rows="4" value={formData.description || ''} onChange={handleChange}></textarea></div>
                </fieldset>
            </div>
            <div className="modal-footer">
                <button onClick={onClose} className="btn-cancel" disabled={isSubmitting}>Cancel</button>
                <button onClick={handleSaveChanges} className="btn-save" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
        </div></div>
    );
};
const ConfirmationModal = ({ onConfirm, onCancel, isSubmitting, title, message }) => {
    return (
        <div className="modal-backdrop"><div className="modal-content">
            <h2>{title || "Confirm Deletion"}</h2>
            <p>{message || "Are you sure you want to permanently delete this unverified report? This action cannot be undone."}</p>
            <div className="modal-footer">
                <button onClick={onCancel} className="btn-cancel" disabled={isSubmitting}>Cancel</button>
                <button onClick={onConfirm} className="btn-delete-confirm" disabled={isSubmitting}>{isSubmitting ? 'Deleting...' : 'Confirm Delete'}</button>
            </div>
        </div></div>
    );
};
const DuplicateWarningModal = ({ onConfirm, onCancel, isSubmitting }) => {
    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <h2>Potential Duplicate Detected</h2>
                <div className="warning-text">
                   <p>This report has the same date and district as another report.</p>
                   <p>Please double-check the details before verifying.</p>
                </div>
                <div className="modal-footer">
                    <button onClick={onCancel} className="btn-cancel" disabled={isSubmitting}>Cancel</button>
                    <button onClick={onConfirm} className="btn-verify" disabled={isSubmitting}>Verify Anyway</button>
                </div>
            </div>
        </div>
    );
};
const BulkUploadModal = ({ onClose, isSubmitting, setSubmitting, onDownloadTemplate }) => {
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('');
    const [errorDetails, setErrorDetails] = useState([]);
    const [uploadCompleteMessage, setUploadCompleteMessage] = useState('');
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setMessage('');
        setErrorDetails([]);
        setUploadCompleteMessage(''); 
    };

    const handleUpload = async () => {
        if (!file) {
            setMessage('Please select a file first.');
            return;
        }
        setSubmitting(true);
        setErrorDetails([]);
        setUploadCompleteMessage('');
        setMessage('Starting file processing...');

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const csvData = event.target.result;
                const rows = csvData.split(/\r?\n/);
                const header = rows.shift().split(',').map(h => h.trim());

                const batch = writeBatch(db);
                let successCount = 0;
                let errorCount = 0;
                let tempErrorDetails = [];

                setMessage(`Processing ${rows.length - 1} data rows...`);

                const parseCsvRow = (row) => {
                    const columns = [];
                    let currentColumn = '';
                    let inQuotes = false;
                    for (let i = 0; i < row.length; i++) {
                        const char = row[i];
                        if (char === '"' && (i === 0 || row[i-1] !== '\\')) {
                            inQuotes = !inQuotes;
                        } else if (char === ',' && !inQuotes) {
                            columns.push(currentColumn.trim());
                            currentColumn = '';
                        } else {
                            currentColumn += char;
                        }
                    }
                    columns.push(currentColumn.trim());
                    return columns.map(c => c.startsWith('"') && c.endsWith('"') ? c.slice(1, -1).replace(/""/g, '"') : c);
                };

                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    if (row.trim() === '') continue;

                    try {
                        const columns = parseCsvRow(row);
                        
                        if (columns.length > header.length) {
                             throw new Error(`Too many columns. Expected ${header.length}, but got ${columns.length}. Please check for unquoted commas.`);
                        }
                        
                        const [datetime, region, district, ward, location, source, totalInjuries, totalFatalities, sourceDetails, description, vehicleTypes] = columns;
                        
                        if (!datetime || !region || !district || !location) {
                            throw new Error("Missing required fields (datetime, region, district, location).");
                        }

                        const newDocRef = doc(collection(db, 'accidents'));
                        const vehiclesArray = vehicleTypes ? vehicleTypes.split('|').map(type => ({ type: type.trim(), registration: '' })) : [{ type: 'Unknown', registration: '' }];
                        
                        batch.set(newDocRef, {
                            datetime: Timestamp.fromDate(new Date(datetime)),
                            region: region.trim(), 
                            district: district.trim(), 
                            ward: ward ? ward.trim() : '',
                            location: location.trim(), 
                            source: source ? source.trim() : 'Unknown',
                            totalInjuries: Number(totalInjuries) || 0,
                            totalFatalities: Number(totalFatalities) || 0,
                            sourceDetails: sourceDetails ? sourceDetails.trim() : '',
                            description: description ? description.trim() : '',
                            vehicles: vehiclesArray,
                            isVerified: false, 
                            submittedAt: Timestamp.now(),
                        });
                        successCount++;
                    } catch (rowError) {
                        errorCount++;
                        const errorMsg = `Row ${i + 2}: ${rowError.message}`;
                        console.error(errorMsg, "Row content:", row);
                        tempErrorDetails.push(errorMsg);
                    }
                }
                
                setErrorDetails(tempErrorDetails);

                if (successCount > 0) {
                     setMessage(`Attempting to commit ${successCount} valid records to the database...`);
                     await batch.commit();
                }
                
                setMessage('');
                setUploadCompleteMessage(`${successCount} records uploaded successfully. ${errorCount} rows had errors.`);
                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }


            } catch (e) {
                setMessage('A critical error occurred during file processing. Check the console for details.');
                setUploadCompleteMessage('');
                console.error("Critical file processing error:", e);
            } finally {
                setSubmitting(false);
            }
        };

        reader.onerror = () => {
            setMessage('Failed to read the file.');
            setUploadCompleteMessage('');
            setSubmitting(false);
        };

        reader.readAsText(file);
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <h2>Bulk Upload Accident Reports</h2>
                <div className="modal-body">
                    <p>Select a CSV file to upload. The file must have the following columns in order:</p>
                    <code className="code-block">datetime,region,district,ward,location,source,totalInjuries,totalFatalities,sourceDetails,description,vehicleTypes</code>
                    
                    <div className="template-download-section">
                         <p>Click the button below to download a CSV template with the required headers.</p>
                         <button onClick={onDownloadTemplate} className="btn-download-template">Download Template</button>
                    </div>

                    <p><strong>Important:</strong> If any text field (like `description`) contains a comma, it must be enclosed in double quotes (e.g., `"A collision occurred, and the road was blocked."`).</p>
                    <div className="form-group" style={{marginTop: '20px'}}><label>CSV File</label><input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileChange} /></div>
                    
                    {message && !uploadCompleteMessage && <p className={`upload-message ${errorDetails.length > 0 ? 'error' : ''}`}>{message}</p>}
                    {uploadCompleteMessage &&
                        <div className="upload-success-box">
                            <p>{uploadCompleteMessage}</p>
                        </div>
                    }

                    {errorDetails.length > 0 && (
                        <div className="error-details-box">
                            <h4>Upload Errors:</h4>
                            <ul>
                                {errorDetails.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                                {errorDetails.length > 10 && <li>...and {errorDetails.length - 10} more errors. Check console for full list.</li>}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn-cancel" disabled={isSubmitting}>Cancel</button>
                    <button onClick={handleUpload} className="btn-save" disabled={isSubmitting || uploadCompleteMessage}>{isSubmitting ? 'Processing...' : 'Upload File'}</button>
                </div>
            </div>
        </div>
    );
};


function AdminDashboard() {
    const { user, userRole, loading: authLoading } = useAuth();
    const [accidents, setAccidents] = useState([]);
    const [hazards, setHazards] = useState([]);
    const [activeTab, setActiveTab] = useState('accidents');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [chartFilter, setChartFilter] = useState(null);
    const [savedViews, setSavedViews] = useState([]);
    const [selectedAccident, setSelectedAccident] = useState(null);
    const [editingAccident, setEditingAccident] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [deletingEntityType, setDeletingEntityType] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'datetime', direction: 'descending' });
    const [selectedIds, setSelectedIds] = useState([]);
    const [lastSelectedId, setLastSelectedId] = useState(null); 
    const [bulkAction, setBulkAction] = useState('');
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
    const [verifyingAccident, setVerifyingAccident] = useState(null);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [reportsToMerge, setReportsToMerge] = useState([]);

    const regionChartRef = useRef();
    const districtChartRef = useRef();
    const sourceChartRef = useRef();
    const vehicleChartRef = useRef();


    const truncateEmail = (email) => {
        if (!email || !email.includes('@')) {
            return email || '...';
        }
        const atIndex = email.indexOf('@');
        const username = email.substring(0, atIndex);
        const domain = email.substring(atIndex + 1);
        
        const lastDotIndex = domain.lastIndexOf('.');
        
        if (lastDotIndex === -1 || lastDotIndex > domain.length - 2) {
            return `${username}@...`;
        }
        
        const tld = domain.substring(lastDotIndex + 1);
        const lastTwo = tld.slice(-2);
        
        return `${username}@...${lastTwo}`;
    };

    useEffect(() => {
        const unsubscribeAccidents = onSnapshot(collection(db, 'accidents'), (snapshot) => {
            const accidentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), datetime: doc.data().datetime?.toDate() }));
            setAccidents(accidentsData);
        });
        
        const unsubscribeHazards = onSnapshot(collection(db, 'hazards'), (snapshot) => {
            const hazardsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), submittedAt: doc.data().submittedAt?.toDate() }));
            setHazards(hazardsData);
        });

        const loadedViews = JSON.parse(localStorage.getItem('trard-saved-views')) || [];
        setSavedViews(loadedViews);
        return () => {
            unsubscribeAccidents();
            unsubscribeHazards();
        };
    }, []);

    const handleSaveView = () => {
        const viewName = prompt("Enter a name for this filter view:");
        if (viewName) {
            const newView = {
                name: viewName,
                filters: { searchTerm, statusFilter, startDate, endDate, chartFilter }
            };
            const updatedViews = [...savedViews, newView];
            setSavedViews(updatedViews);
            localStorage.setItem('trard-saved-views', JSON.stringify(updatedViews));
        }
    };

    const applySavedView = (view) => {
        setSearchTerm(view.filters.searchTerm || '');
        setStatusFilter(view.filters.statusFilter || 'All');
        setStartDate(view.filters.startDate || '');
        setEndDate(view.filters.endDate || '');
        setChartFilter(view.filters.chartFilter || null);
    };

    const deleteSavedView = (viewName) => {
        if (window.confirm(`Are you sure you want to delete the view "${viewName}"?`)) {
            const updatedViews = savedViews.filter(v => v.name !== viewName);
            setSavedViews(updatedViews);
            localStorage.setItem('trard-saved-views', JSON.stringify(updatedViews));
        }
    };

    const handleDownloadTemplate = () => {
        const header = ['datetime', 'region', 'district', 'ward', 'location', 'source', 'totalInjuries', 'totalFatalities', 'sourceDetails', 'description', 'vehicleTypes'];
        const csvContent = "data:text/csv;charset=utf-8," + header.join(',');
    
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "TRARD_bulk_upload_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const duplicateGroups = useMemo(() => {
        const potentialDuplicates = {};
        accidents.forEach(acc => {
            if (!acc.datetime || !acc.district) return;
            const dateStr = acc.datetime.toISOString().split('T')[0];
            const key = `${dateStr}_${acc.district}`;
            if (!potentialDuplicates[key]) {
                potentialDuplicates[key] = [];
            }
            potentialDuplicates[key].push(acc.id);
        });
        const duplicateSets = {};
        let colorIndex = 1;
        Object.values(potentialDuplicates).forEach(idGroup => {
            if (idGroup.length > 1) {
                idGroup.forEach(id => {
                    duplicateSets[id] = `duplicate-group-${colorIndex}`;
                });
                colorIndex = (colorIndex % 5) + 1;
            }
        });
        return duplicateSets;
    }, [accidents]);

    const isMergePossible = useMemo(() => {
        if (selectedIds.length < 2) return false;
        if(userRole !== 'Admin' && userRole !== 'Super Admin') return false;
        const firstGroup = duplicateGroups[selectedIds[0]];
        if (!firstGroup) return false;
        return selectedIds.every(id => duplicateGroups[id] === firstGroup);
    }, [selectedIds, duplicateGroups, userRole]);

    const sortedAndFilteredAccidents = useMemo(() => {
        let sortableItems = [...accidents];

        if (chartFilter) {
            sortableItems = sortableItems.filter(accident => {
                if (chartFilter.type === 'region') {
                    return accident.region === chartFilter.value;
                }
                if (chartFilter.type === 'district') {
                    return accident.district === chartFilter.value;
                }
                if (chartFilter.type === 'source') {
                    return accident.source === chartFilter.value;
                }
                if (chartFilter.type === 'vehicle') {
                    return accident.vehicles?.some(v => (typeof v === 'string' ? v : v.type) === chartFilter.value);
                }
                return true;
            });
        }

        sortableItems = sortableItems.filter(accident => {
            const lowercasedTerm = searchTerm.toLowerCase();
            const matchesSearch = searchTerm ? (
                (accident.id?.toLowerCase().includes(lowercasedTerm)) ||
                (accident.location?.toLowerCase().includes(lowercasedTerm)) ||
                (accident.region?.toLowerCase().includes(lowercasedTerm)) ||
                (accident.district?.toLowerCase().includes(lowercasedTerm))
            ) : true;
            const matchesStatus = statusFilter !== 'All' ? (accident.isVerified === (statusFilter === 'Verified')) : true;
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;
            if (start) start.setHours(0, 0, 0, 0);
            if (end) end.setHours(23, 59, 59, 999);
            const accidentDate = accident.datetime;
            const matchesDate = !accidentDate ? false : (!start || accidentDate >= start) && (!end || accidentDate <= end);
            return matchesSearch && matchesStatus && matchesDate;
        });
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                
                if (sortConfig.key === 'totalInjuries') {
                    aValue = (a.totalInjuries > 0) ? a.totalInjuries : (a.injuriesMale || 0) + (a.injuriesFemale || 0) + (a.injuriesChildren || 0);
                    bValue = (b.totalInjuries > 0) ? b.totalInjuries : (b.injuriesMale || 0) + (b.injuriesFemale || 0) + (b.injuriesChildren || 0);
                }
                 if (sortConfig.key === 'totalFatalities') {
                    aValue = (a.totalFatalities > 0) ? a.totalFatalities : (a.fatalitiesMale || 0) + (a.fatalitiesFemale || 0) + (a.fatalitiesChildren || 0);
                    bValue = (b.totalFatalities > 0) ? b.totalFatalities : (b.fatalitiesMale || 0) + (b.fatalitiesFemale || 0) + (b.fatalitiesChildren || 0);
                }

                if (aValue < bValue) { return sortConfig.direction === 'ascending' ? -1 : 1; }
                if (aValue > bValue) { return sortConfig.direction === 'ascending' ? 1 : -1; }
                return 0;
            });
        }
        return sortableItems;
    }, [searchTerm, statusFilter, startDate, endDate, accidents, sortConfig, chartFilter]);

    const handleVerification = useCallback(async (accident, currentStatus) => {
        if (duplicateGroups[accident.id] && !currentStatus) {
            setVerifyingAccident(accident);
            setShowDuplicateWarning(true);
        } else {
            const accidentRef = doc(db, 'accidents', accident.id);
            await updateDoc(accidentRef, { isVerified: !currentStatus });
            await logAction(!currentStatus ? 'Verify Report' : 'Un-verify Report', accident.id, user?.email);
        }
    }, [duplicateGroups, user]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                return;
            }

            const isModalOpen = selectedAccident || editingAccident || deletingId || showMergeModal || showUploadModal || showDuplicateWarning;

            if (e.key === 'Escape' && isModalOpen) {
                setSelectedAccident(null);
                setEditingAccident(null);
                setDeletingId(null);
                setShowMergeModal(false);
                setShowUploadModal(false);
                setShowDuplicateWarning(false);
                return;
            }
            
            if (isModalOpen || activeTab !== 'accidents') return;

            if (selectedIds.length === 1) {
                const selectedReport = sortedAndFilteredAccidents.find(acc => acc.id === selectedIds[0]);
                if (!selectedReport) return;

                switch (e.key.toLowerCase()) {
                    case 'v':
                        if (userRole === 'Admin' || userRole === 'Super Admin') {
                            e.preventDefault();
                            handleVerification(selectedReport, selectedReport.isVerified);
                        }
                        break;
                    case 'e':
                        e.preventDefault();
                        setEditingAccident(selectedReport);
                        break;
                    case 'd':
                         if (userRole === 'Admin' || userRole === 'Super Admin') {
                            e.preventDefault();
                            setDeletingId(selectedReport.id);
                            setDeletingEntityType('accident');
                         }
                        break;
                    default:
                        break;
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedIds, sortedAndFilteredAccidents, userRole, handleVerification, activeTab, selectedAccident, editingAccident, deletingId, showMergeModal, showUploadModal, showDuplicateWarning]);


    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setSelectedIds([]);
    };
    
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allVisibleIds = sortedAndFilteredAccidents.map(acc => acc.id);
            setSelectedIds(allVisibleIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (clickedId, event) => {
        const { shiftKey, ctrlKey, metaKey } = event;

        if (shiftKey && lastSelectedId) {
            const lastIndex = sortedAndFilteredAccidents.findIndex(item => item.id === lastSelectedId);
            const currentIndex = sortedAndFilteredAccidents.findIndex(item => item.id === clickedId);
            
            const start = Math.min(lastIndex, currentIndex);
            const end = Math.max(lastIndex, currentIndex);
            
            const rangeIds = sortedAndFilteredAccidents.slice(start, end + 1).map(item => item.id);
            
            const newSelectedIds = [...selectedIds];
            rangeIds.forEach(id => {
                if (!newSelectedIds.includes(id)) {
                    newSelectedIds.push(id);
                }
            });
            setSelectedIds(newSelectedIds);

        } else if (ctrlKey || metaKey) {
            setSelectedIds(prev => 
                prev.includes(clickedId) 
                    ? prev.filter(id => id !== clickedId) 
                    : [...prev, clickedId]
            );
            setLastSelectedId(clickedId); 
        } else {
            setSelectedIds([clickedId]);
            setLastSelectedId(clickedId);
        }
    };

    const handleApplyBulkAction = async () => {
        if (!bulkAction || selectedIds.length === 0) return;
        
        if (bulkAction === 'merge') {
            if (!isMergePossible) {
                alert("Merge is only possible for 2 or more reports from the same duplicate group.");
                return;
            }
            const reports = selectedIds.map(id => accidents.find(acc => acc.id === id));
            setReportsToMerge(reports);
            setShowMergeModal(true);
            return;
        }

        if (bulkAction === 'delete') {
            setShowBulkDeleteConfirm(true);
            return;
        }

        const isVerifiedValue = bulkAction === 'verify';
        setIsSubmitting(true);
        try {
            const batch = writeBatch(db);
            selectedIds.forEach(id => {
                const docRef = doc(db, 'accidents', id);
                batch.update(docRef, { isVerified: isVerifiedValue });
                logAction(isVerifiedValue ? 'Bulk Verify' : 'Bulk Un-verify', id, user?.email);
            });
            await batch.commit();
            setSelectedIds([]);
            setBulkAction('');
        } catch (error) {
            console.error("Bulk update failed: ", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmBulkDelete = async () => {
        setIsSubmitting(true);
        try {
            const batch = writeBatch(db);
            selectedIds.forEach(id => {
                const docRef = doc(db, 'accidents', id);
                batch.delete(docRef);
                logAction('Bulk Delete', id, user?.email);
            });
            await batch.commit();
            setSelectedIds([]);
            setBulkAction('');
        } catch (error) {
            console.error("Bulk delete failed: ", error);
        } finally {
            setIsSubmitting(false);
            setShowBulkDeleteConfirm(false);
        }
    };

    const handleSaveEdit = async (updatedAccident) => {
        setIsSubmitting(true);
        const { id, ...dataToSave } = updatedAccident;
        const accidentRef = doc(db, 'accidents', id);
        try {
            await updateDoc(accidentRef, dataToSave);
            await logAction('Edit Report', id, user?.email);
            setEditingAccident(null);
        } catch (error) {
            console.error("Error updating document: ", error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleSaveMerge = async (mergedData) => {
        setIsSubmitting(true);
        const batch = writeBatch(db);
        const originalIds = reportsToMerge.map(r => r.id);
        
        const newDocRef = doc(collection(db, 'accidents'));
        
        for (const oldId of originalIds) {
            const docRef = doc(db, 'accidents', oldId);
            batch.delete(docRef);
            await logAction('Report Merged', oldId, user?.email, { mergedInto: newDocRef.id });
        }
        
        batch.set(newDocRef, mergedData);
        
        try {
            await batch.commit();
            await logAction('Created via Merge', newDocRef.id, user?.email, { mergedFrom: originalIds });
        } catch (error) {
            console.error("Error merging documents: ", error);
        } finally {
            setIsSubmitting(false);
            setShowMergeModal(false);
            setSelectedIds([]);
            setReportsToMerge([]);
            setBulkAction('');
        }
    };


    const handleDelete = async () => {
        if (!deletingId) return;
        setIsSubmitting(true);
        const collectionName = deletingEntityType === 'hazard' ? 'hazards' : 'accidents';
        try {
            await deleteDoc(doc(db, collectionName, deletingId));
            await logAction(`Delete ${deletingEntityType}`, deletingId, user?.email);
            setDeletingId(null);
            setDeletingEntityType(null);
        } catch (error) {
            console.error("Error deleting document: ", error);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const proceedWithVerification = async () => {
        if (!verifyingAccident) return;
        const accidentRef = doc(db, 'accidents', verifyingAccident.id);
        await updateDoc(accidentRef, { isVerified: !verifyingAccident.isVerified });
        await logAction('Verify Report (Duplicate Warning Ignored)', verifyingAccident.id, user?.email);
        setShowDuplicateWarning(false);
        setVerifyingAccident(null);
    };

    const handleVerifyHazard = async (hazardId, currentStatus) => {
        const hazardRef = doc(db, 'hazards', hazardId);
        await updateDoc(hazardRef, { isVerified: !currentStatus });
        await logAction(!currentStatus ? 'Verify Hazard' : 'Un-verify Hazard', hazardId, user?.email);
    };

    const handleResolveHazard = async (hazardId, currentStatus) => {
        const hazardRef = doc(db, 'hazards', hazardId);
        await updateDoc(hazardRef, { isResolved: !currentStatus });
        await logAction(!currentStatus ? 'Resolve Hazard' : 'Un-resolve Hazard', hazardId, user?.email);
    };
    
    const dashboardStats = useMemo(() => {
        const unverifiedReports = accidents.filter(acc => !acc.isVerified).length;
        const totalInjuries = accidents.reduce((sum, acc) => sum + ((acc.totalInjuries > 0) ? acc.totalInjuries : (acc.injuriesMale || 0) + (acc.injuriesFemale || 0) + (acc.injuriesChildren || 0)), 0);
        const totalFatalities = accidents.reduce((sum, acc) => sum + ((acc.totalFatalities > 0) ? acc.totalFatalities : (acc.fatalitiesMale || 0) + (acc.fatalitiesFemale || 0) + (acc.fatalitiesChildren || 0)), 0);
        return { totalReports: accidents.length, unverifiedReports, totalInjuries, totalFatalities };
    }, [accidents]);
    
    const timeChartData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            if (curr.datetime) {
                const month = curr.datetime.toLocaleString('en-US', { month: 'short', year: 'numeric' });
                acc[month] = (acc[month] || 0) + 1;
            }
            return acc;
        }, {});
        const sortedLabels = Object.keys(counts).sort((a, b) => new Date(a) - new Date(b));
        return {
            labels: sortedLabels,
            datasets: [{ label: 'Accidents per Month', data: sortedLabels.map(label => counts[label]), borderColor: 'rgb(75, 192, 192)', backgroundColor: 'rgba(75, 192, 192, 0.5)', tension: 0.1 }]
        };
    }, [accidents]);

    const sourceChartData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            const source = curr.source || 'Unknown';
            acc[source] = (acc[source] || 0) + 1;
            return acc;
        }, {});
        return {
            labels: Object.keys(counts),
            datasets: [{ data: Object.values(counts), backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'] }]
        };
    }, [accidents]);

    const casualtyByRegionChartData = useMemo(() => {
        const data = accidents.reduce((acc, curr) => {
            const region = curr.region || 'Unknown';
            if (!acc[region]) acc[region] = { injuries: 0, fatalities: 0 };
            acc[region].injuries += (curr.totalInjuries > 0) ? curr.totalInjuries : (curr.injuriesMale || 0) + (acc.injuriesFemale || 0) + (acc.injuriesChildren || 0);
            acc[region].fatalities += (curr.totalFatalities > 0) ? acc.totalFatalities : (acc.fatalitiesMale || 0) + (acc.fatalitiesFemale || 0) + (acc.fatalitiesChildren || 0);
            return acc;
        }, {});
        const labels = Object.keys(data);
        return {
            labels,
            datasets: [
                { label: 'Total Injuries', data: labels.map(label => data[label].injuries), backgroundColor: 'rgba(255, 159, 64, 0.7)' },
                { label: 'Total Fatalities', data: labels.map(label => data[label].fatalities), backgroundColor: 'rgba(255, 99, 132, 0.7)' },
            ]
        };
    }, [accidents]);
    
    const districtHistogramData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            if (curr.district) acc[curr.district] = (acc[curr.district] || 0) + 1;
            return acc;
        }, {});
        const sortedDistricts = Object.entries(counts).sort(([,a],[,b]) => b-a).slice(0, 10);
        return {
            labels: sortedDistricts.map(d => d[0]),
            datasets: [{ label: 'Top 10 Districts by Accident Count', data: sortedDistricts.map(d => d[1]), backgroundColor: 'rgba(153, 102, 255, 0.6)' }]
        };
    }, [accidents]);

    const regionHistogramData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            if (curr.region) acc[curr.region] = (acc[curr.region] || 0) + 1;
            return acc;
        }, {});
        const sortedRegions = Object.entries(counts).sort(([,a],[,b]) => b-a).slice(0, 10);
        return {
            labels: sortedRegions.map(r => r[0]),
            datasets: [{ label: 'Top 10 Regions by Accident Count', data: sortedRegions.map(r => r[1]), backgroundColor: 'rgba(54, 162, 235, 0.6)' }]
        };
    }, [accidents]);

    const vehiclePieChartData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            curr.vehicles?.forEach(vehicle => {
                const type = typeof vehicle === 'string' ? vehicle : vehicle.type || 'Unknown';
                acc[type] = (acc[type] || 0) + 1;
            });
            return acc;
        }, {});
        return {
            labels: Object.keys(counts),
            datasets: [{ data: Object.values(counts), backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9CBCF', '#4D5360'], hoverOffset: 4 }]
        };
    }, [accidents]);

    const injuriesVsDeathsChartData = useMemo(() => {
        return {
            labels: ['Total Injuries', 'Total Fatalities'],
            datasets: [{
                data: [dashboardStats.totalInjuries, dashboardStats.totalFatalities],
                backgroundColor: ['rgba(255, 159, 64, 0.8)', 'rgba(255, 99, 132, 0.8)'],
                hoverOffset: 4
            }]
        };
    }, [dashboardStats]);

    const handleChartClick = (event, chartRef, filterType) => {
        if (!chartRef.current) return;
        const element = getElementAtEvent(chartRef.current, event);
        if (!element.length) return;

        const { index } = element[0];
        const data = chartRef.current.data;
        const clickedLabel = data.labels[index];

        setChartFilter({ type: filterType, value: clickedLabel });
    };


    if (authLoading) {
        return <div>Loading dashboard...</div>
    }

    return (
        <div className="admin-dashboard">
            <div className="dashboard-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'accidents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('accidents')}
                >
                    Accident Reports
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'hazards' ? 'active' : ''}`}
                    onClick={() => setActiveTab('hazards')}
                >
                    Hazard Reports
                </button>
            </div>

            {activeTab === 'accidents' && (
                <>
                    {selectedAccident && <AccidentDetailsModal accident={selectedAccident} onClose={() => setSelectedAccident(null)} />}
                    {editingAccident && <EditAccidentModal accident={editingAccident} onSave={handleSaveEdit} onClose={() => setEditingAccident(null)} isSubmitting={isSubmitting} />}
                    {deletingId && deletingEntityType === 'accident' && <ConfirmationModal onConfirm={handleDelete} onCancel={() => setDeletingId(null)} />}
                    {showBulkDeleteConfirm && (
                        <ConfirmationModal 
                            onConfirm={handleConfirmBulkDelete}
                            onCancel={() => setShowBulkDeleteConfirm(false)}
                            isSubmitting={isSubmitting}
                            title="Confirm Bulk Deletion"
                            message={`Are you sure you want to permanently delete ${selectedIds.length} selected reports? This action cannot be undone.`}
                        />
                    )}
                    {showUploadModal && <BulkUploadModal onClose={() => setShowUploadModal(false)} isSubmitting={isSubmitting} setSubmitting={setIsSubmitting} onDownloadTemplate={handleDownloadTemplate} />}
                    {showDuplicateWarning && <DuplicateWarningModal onConfirm={proceedWithVerification} onCancel={() => setShowDuplicateWarning(false)} />}
                    {showMergeModal && <MergeModal reports={reportsToMerge} onSave={handleSaveMerge} onClose={() => setShowMergeModal(false)} isSubmitting={isSubmitting} />}
                    
                    <div className="dashboard-header">
                        <h1>Accident Dashboard</h1>
                        {(userRole === 'Admin' || userRole === 'Super Admin') && 
                            <button className="btn-upload" onClick={() => setShowUploadModal(true)}>Bulk Upload Reports</button>
                        }
                    </div>
                    
                    <p>Signed in as: <strong>{truncateEmail(user?.email)}</strong> | Role: <strong>{userRole}</strong></p>

                    <div className="stats-container">
                        <div className="stat-card"><h4>Total Reports</h4><p>{dashboardStats.totalReports}</p></div>
                        <div className="stat-card"><h4>Unverified Reports</h4><p>{dashboardStats.unverifiedReports}</p></div>
                        <div className="stat-card"><h4>Total Injuries</h4><p>{dashboardStats.totalInjuries}</p></div>
                        <div className="stat-card"><h4>Total Fatalities</h4><p>{dashboardStats.totalFatalities}</p></div>
                    </div>
                    
                    <div className="filters-container">
                        <input type="text" placeholder="Search..." className="search-bar" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <div className="filter-group"><label>Status:</label><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="All">All</option><option value="Verified">Verified</option><option value="Unverified">Unverified</option></select></div>
                        <div className="filter-group"><label>From:</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
                        <div className="filter-group"><label>To:</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
                        {chartFilter && (
                            <div className="chart-filter-active">
                                <span>Filtering by {chartFilter.type}: <strong>{chartFilter.value}</strong></span>
                                <button onClick={() => setChartFilter(null)}>Clear Chart Filter</button>
                            </div>
                        )}
                    </div>
                    <div className="filters-container">
                         <div className="filter-group">
                            <button onClick={handleSaveView} className="btn-secondary">Save View</button>
                        </div>
                        {savedViews.length > 0 && (
                            <div className="filter-group">
                                <label>Load View:</label>
                                <select className="saved-views-dropdown" onChange={(e) => {
                                    const viewName = e.target.value;
                                    if (viewName) {
                                        const viewToApply = savedViews.find(v => v.name === viewName);
                                        if (viewToApply) applySavedView(viewToApply);
                                    }
                                }} value="">
                                    <option value="" disabled>Select a view...</option>
                                    {savedViews.map(view => (
                                        <option key={view.name} value={view.name}>{view.name}</option>
                                    ))}
                                </select>
                                 <button onClick={() => {
                                    const dropdown = document.querySelector('.saved-views-dropdown');
                                    if (dropdown && dropdown.value) {
                                        deleteSavedView(dropdown.value);
                                        dropdown.value = "";
                                    }
                                }} className="btn-delete-view" title="Delete selected view">Delete</button>
                            </div>
                        )}
                    </div>
                    
                    <div className="reports-table-container">
                        <h3>Filtered Accident Reports</h3>
                        {selectedIds.length > 0 && (userRole === 'Admin' || userRole === 'Super Admin') && (
                            <div className="bulk-action-bar">
                                <span>{selectedIds.length} selected</span>
                                <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
                                    <option value="">Choose bulk action...</option>
                                    <option value="verify">Verify Selected</option>
                                    <option value="unverify">Un-verify Selected</option>
                                    {isMergePossible && <option value="merge">Merge Selected</option>}
                                    <option value="delete">Delete Selected</option>
                                </select>
                                <button onClick={handleApplyBulkAction} className="btn-apply" disabled={!bulkAction || isSubmitting}>
                                    {isSubmitting ? 'Applying...' : 'Apply'}
                                </button>
                            </div>
                        )}
                        <table>
                          <thead>
                            <tr>
                              <th onClick={() => requestSort('datetime')} className="sortable">Date <span className="sort-indicator">{sortConfig.key === 'datetime' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}</span></th>
                              <th>Location</th>
                              <th>Vehicles</th>
                              <th onClick={() => requestSort('totalInjuries')} className="sortable">Total Inj. <span className="sort-indicator">{sortConfig.key === 'totalInjuries' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}</span></th>
                              <th onClick={() => requestSort('totalFatalities')} className="sortable">Total Fat. <span className="sort-indicator">{sortConfig.key === 'totalFatalities' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}</span></th>
                              <th onClick={() => requestSort('isVerified')} className="sortable">Status <span className="sort-indicator">{sortConfig.key === 'isVerified' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}</span></th>
                              <th>Actions</th>
                              <th>
                                {(userRole === 'Admin' || userRole === 'Super Admin') &&
                                    <input 
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={sortedAndFilteredAccidents.length > 0 && selectedIds.length === sortedAndFilteredAccidents.length}
                                        ref={el => el && (el.indeterminate = selectedIds.length > 0 && selectedIds.length < sortedAndFilteredAccidents.length)}
                                    />
                                }
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedAndFilteredAccidents.map((accident) => {
                              const totalInjuries = accident.totalInjuries > 0 ? accident.totalInjuries : (accident.injuriesMale || 0) + (accident.injuriesFemale || 0) + (accident.injuriesChildren || 0);
                              const totalFatalities = accident.totalFatalities > 0 ? accident.totalFatalities : (accident.fatalitiesMale || 0) + (accident.fatalitiesFemale || 0) + (accident.fatalitiesChildren || 0);
                              const mainVehicle = accident.vehicles && accident.vehicles.length > 0 ? (typeof accident.vehicles[0] === 'string' ? accident.vehicles[0] : accident.vehicles[0].type) : 'N/A';
                              const vehicleCount = accident.vehicles ? accident.vehicles.length : 0;
                              const isSelectable = userRole === 'Admin' || userRole === 'Super Admin';
                              const rowClass = `${isSelectable && selectedIds.includes(accident.id) ? 'selected-row' : ''} ${duplicateGroups[accident.id] || ''}`;
                              
                              return (
                                <tr 
                                    key={accident.id} 
                                    className={rowClass}
                                    onClick={isSelectable ? (e) => handleSelectOne(accident.id, e) : undefined}
                                >
                                  <td>{accident.datetime?.toLocaleDateString()}</td>
                                  <td>{accident.location}</td>
                                  <td title={accident.vehicles?.map(v => typeof v === 'string' ? v : v.type).join(', ')}>{mainVehicle} {vehicleCount > 1 ? `(+${vehicleCount - 1})` : ''}</td>
                                  <td>{totalInjuries}</td>
                                  <td>{totalFatalities}</td>
                                  <td><span className={`status ${accident.isVerified ? 'status-verified' : 'status-unverified'}`}>{accident.isVerified ? 'Verified' : 'Unverified'}</span></td>
                                  <td className="action-buttons" onClick={(e) => e.stopPropagation()}>
                                    <button className="btn-details" onClick={() => setSelectedAccident(accident)}>Details</button>
                                    
                                    <button className="btn-edit" onClick={() => setEditingAccident(accident)}>Edit</button>

                                    {(userRole === 'Admin' || userRole === 'Super Admin') && 
                                        <>
                                            <button className="btn-verify" onClick={() => handleVerification(accident, accident.isVerified)}>{accident.isVerified ? 'Un-verify' : 'Verify'}</button>
                                            <button className="btn-delete" onClick={() => {setDeletingId(accident.id); setDeletingEntityType('accident');}}>Delete</button>
                                        </>
                                    }
                                  </td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    {isSelectable &&
                                        <input 
                                            type="checkbox"
                                            checked={selectedIds.includes(accident.id)}
                                            onClick={(e) => handleSelectOne(accident.id, e)}
                                            onChange={() => {}}
                                        />
                                    }
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                    </div>
                    
                    <div className="charts-section">
                        <div className="chart-container">
                            <h3>Accidents Over Time</h3>
                            <Line data={timeChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </div>
                         <div className="chart-container">
                            <h3>Accidents by Vehicle Type</h3>
                            <Pie 
                                ref={vehicleChartRef}
                                data={vehiclePieChartData} 
                                options={{ responsive: true, maintainAspectRatio: false }} 
                                onClick={(event) => handleChartClick(event, vehicleChartRef, 'vehicle')}
                            />
                        </div>
                    </div>
                     <div className="charts-section">
                        <div className="chart-container">
                            <h3>Accidents by Source</h3>
                            <Doughnut 
                                ref={sourceChartRef}
                                data={sourceChartData} 
                                options={{ responsive: true, maintainAspectRatio: false }}
                                onClick={(event) => handleChartClick(event, sourceChartRef, 'source')}
                            />
                        </div>
                         <div className="chart-container">
                            <h3>Injuries vs. Deaths</h3>
                            <Pie data={injuriesVsDeathsChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </div>
                    </div>
                    <div className="charts-section">
                        <div className="chart-container">
                            <h3>Top 10 Regions</h3>
                            <Bar 
                                ref={regionChartRef}
                                data={regionHistogramData} 
                                options={{ responsive: true, maintainAspectRatio: false }} 
                                onClick={(event) => handleChartClick(event, regionChartRef, 'region')}
                            />
                        </div>
                         <div className="chart-container">
                            <h3>Top 10 Districts</h3>
                            <Bar 
                                ref={districtChartRef}
                                data={districtHistogramData} 
                                options={{ responsive: true, maintainAspectRatio: false }} 
                                onClick={(event) => handleChartClick(event, districtChartRef, 'district')}
                            />
                        </div>
                    </div>
                     <div className="charts-section">
                        <div className="chart-container large">
                            <h3>Casualties by Region</h3>
                            <Bar data={casualtyByRegionChartData} options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false }} />
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'hazards' && (
                <div className="reports-table-container">
                    {deletingId && deletingEntityType === 'hazard' && <ConfirmationModal onConfirm={handleDelete} onCancel={() => setDeletingId(null)} title="Delete Hazard" message="Are you sure you want to permanently delete this hazard report?"/>}
                    <h3>Road Hazard Reports</h3>
                     <table className="hazard-table">
                        <thead>
                            <tr>
                                <th className="col-date">Reported On</th>
                                <th className="col-type">Type</th>
                                <th className="col-location">Location</th>
                                <th className="col-description">Description</th>
                                <th className="col-status">Status</th>
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {hazards.map(hazard => (
                                <tr key={hazard.id}>
                                    <td>{hazard.submittedAt?.toLocaleDateString()}</td>
                                    <td>{hazard.hazardType}</td>
                                    <td className="truncate">{`${hazard.location}, ${hazard.district}, ${hazard.region}`}</td>
                                    <td className="truncate">{hazard.description}</td>
                                    <td>
                                        <span className={`status ${hazard.isResolved ? 'status-verified' : (hazard.isVerified ? 'status-unverified' : 'status-pending')}`}>
                                            {hazard.isResolved ? 'Resolved' : (hazard.isVerified ? 'Verified' : 'Pending Verification')}
                                        </span>
                                    </td>
                                    <td className="action-buttons">
                                        {!hazard.isVerified && (
                                            <button 
                                                className="btn-verify"
                                                onClick={() => handleVerifyHazard(hazard.id, hazard.isVerified)}
                                            >
                                                Verify
                                            </button>
                                        )}
                                        {hazard.isVerified && (
                                            <button 
                                                className="btn-resolve"
                                                onClick={() => handleResolveHazard(hazard.id, hazard.isResolved)}
                                            >
                                                {hazard.isResolved ? 'Mark as Unresolved' : 'Mark as Resolved'}
                                            </button>
                                        )}
                                        <button 
                                            className="btn-delete"
                                            onClick={() => {setDeletingId(hazard.id); setDeletingEntityType('hazard');}}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard;
